import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { payCommissionWithBrowserWallet } from '../utils/hlUsdSend';
import { approveBuilderFeeWithBrowserWallet } from '../utils/hlAgentSign';
import { fmtUsd } from '../utils/fmtPrice';
import { isHlDepositRequiredError, parseApiError } from '../utils/hlDepositMessages';
import HlFundingGuide from './HlFundingGuide';
import './ManualSignalAccessPanel.css';
import './HlFundingGuide.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
});

export default function ManualSignalAccessPanel({ onAccessGranted, onStatusChange, refreshKey = 0 }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [manualWalletId, setManualWalletId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [subRes, walletRes] = await Promise.all([
        axios.get(`${API_BASE}/multi-asset/signal-subscription/status`, { headers: authHeaders() }),
        axios.get(`${API_BASE}/multi-asset/wallets`, { headers: authHeaders() }).catch(() => ({ data: { wallets: [] } })),
      ]);
      setStatus(subRes.data);
      onStatusChange?.(subRes.data);
      const wallets = walletRes.data?.wallets || [];
      const active =
        wallets.find((w) => w.manualConsentValid) ||
        wallets.find((w) => w.builderFeeApproved) ||
        wallets[0];
      setManualWalletId(active?.id ?? null);
    } catch (e) {
      setStatus(null);
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [onStatusChange]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  useEffect(() => {
    if (status?.hasAccess && !status?.isAdmin) {
      onAccessGranted?.();
    }
  }, [status, onAccessGranted]);

  const ensureBuilderApproval = async () => {
    if (status?.builderFeeApproved) return true;
    if (!manualWalletId) {
      throw new Error('Link your Hyperliquid wallet first (gear icon), then retry.');
    }
    if (!status?.walletAddress) {
      throw new Error('Link your Hyperliquid wallet first (gear icon), then retry.');
    }
    const ok = window.confirm(
      'Before purchasing signal access you must approve the platform commission (builder fee) with one wallet signature. You only pay this fee when you execute trades through us — not for viewing signals.\n\nContinue to sign?'
    );
    if (!ok) return false;
    await approveBuilderFeeWithBrowserWallet({
      apiBase: API_BASE,
      authHeaders,
      walletId: manualWalletId,
      walletAddress: status.walletAddress,
    });
    await load();
    return true;
  };

  const purchasePlan = async (plan) => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const price = plan === 'weekly' ? status?.weeklyUsd : status?.monthlyUsd;
      if (!(Number(price) >= 0.01)) {
        throw new Error(`${plan === 'weekly' ? 'Weekly' : 'Monthly'} signal price is not configured yet.`);
      }

      const builderOk = await ensureBuilderApproval();
      if (!builderOk) {
        setError('Builder fee approval is required to view MAP signals.');
        return;
      }

      const fresh = await axios.get(`${API_BASE}/multi-asset/signal-subscription/status`, {
        headers: authHeaders(),
      });
      setStatus(fresh.data);

      const { data: preparePayload } = await axios.post(
        `${API_BASE}/multi-asset/signal-subscription/prepare`,
        { plan },
        { headers: authHeaders() }
      );

      const agreeOk = window.confirm(preparePayload.agreeText || `Pay ${fmtUsd(price)} for ${plan} signal access?`);
      if (!agreeOk) return;

      const signed = await payCommissionWithBrowserWallet({
        walletAddress: fresh.data?.walletAddress,
        preparePayload,
      });

      const { data } = await axios.post(
        `${API_BASE}/multi-asset/signal-subscription/confirm`,
        { plan, ...signed },
        { headers: authHeaders() }
      );

      setSuccess(data.message || 'Signal access activated.');
      await load();
      onAccessGranted?.(data);
    } catch (e) {
      setError(parseApiError(e, status?.walletAddress));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <section className="obot-card manual-signal-access-card">
        <p className="manual-signal-access-loading">Loading signal access…</p>
      </section>
    );
  }

  if (status?.isAdmin) return null;

  if (status?.hasAccess) {
    return (
      <section className="obot-card manual-signal-access-card manual-signal-access-active">
        <h2>MAP signal access</h2>
        <p className="hint">
          <strong>{status.activePlan === 'monthly' ? 'Monthly' : 'Weekly'}</strong> access active until{' '}
          <strong>{status.expiresAtLabel || status.expiresAt}</strong>. Builder and commission fees apply when you
          trade on these signals.
        </p>
        {success && <div className="manual-success">{success}</div>}
      </section>
    );
  }

  return (
    <section className="obot-card manual-signal-access-card">
      <h2>MAP signal access</h2>
      <p className="hint">
        Choose weekly or monthly access to view Omni MAP recommendations on Manual Trades. You will sign a one-time
        builder-fee approval (if not done yet), then pay USDC from your linked Hyperliquid wallet. Trading still
        incurs builder and commission fees on fills.
      </p>

      {error && <div className="obot-error">{error}</div>}
      {success && <div className="manual-success">{success}</div>}

      {!status?.walletAddress && (
        <div className="obot-error">
          Link your Hyperliquid wallet in the gear icon before purchasing signal access.
        </div>
      )}

      {status?.walletAddress && (!status?.builderFeeApproved || isHlDepositRequiredError(error)) && (
        <HlFundingGuide
          walletAddress={status.walletAddress}
          embedded
          defaultTab={isHlDepositRequiredError(error) ? 'checklist' : 'arbitrum'}
          onBalanceChange={() => load()}
        />
      )}

      <div className="manual-signal-access-actions">
        <button
          type="button"
          className="primary-btn manual-signal-plan-btn"
          disabled={busy || !(Number(status?.weeklyUsd) >= 0.01)}
          onClick={() => purchasePlan('weekly')}
        >
          Weekly signals — {fmtUsd(status?.weeklyUsd ?? 0)}
        </button>
        <button
          type="button"
          className="primary-btn manual-signal-plan-btn"
          disabled={busy || !(Number(status?.monthlyUsd) >= 0.01)}
          onClick={() => purchasePlan('monthly')}
        >
          Monthly signals — {fmtUsd(status?.monthlyUsd ?? 0)}
        </button>
      </div>
      <p className="field-hint manual-signal-access-foot">
        Weekly renews each Monday 6:00 AM Central. Monthly renews one month from payment. No MAP signals are shown
        until you complete payment and builder approval.
      </p>
    </section>
  );
}
